import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Empty, Modal, Chip } from "../components/ui-bits";
import {
  Plus, Trash2, Edit3, CalendarClock, ChevronLeft, ChevronRight,
  AlertTriangle, Clock, Search, User, Package, Layers,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useBrand, useBrandPermissions } from "../lib/brand";
import { useT } from "../lib/i18n";
import { useAutoOpenFromQuery } from "../lib/useAutoOpenFromQuery";
import { useNavigate } from "react-router-dom";
import { Factory } from "lucide-react";

function useDeliveries() {
  const [map, setMap] = useState(new Map());
  useEffect(() => {
    api.get("/productions").then(r => {
      const m = new Map();
      for (const p of r.data) {
        if (p.status === "Annullata") continue;
        const d = p.steps.find(s => s.key === "delivery");
        const date = d?.actual_date || d?.planned_date;
        if (!date) continue;
        if (!m.has(date)) m.set(date, []);
        m.get(date).push({ ...p, delivery_date: date, delivered: !!d.actual_date });
      }
      setMap(m);
    }).catch(() => {});
  }, []);
  return map;
}

function DeliveryChip({ p, compact }) {
  const nav = useNavigate();
  const color = p.delivered ? "#15803d" : p.is_late ? "#dc2626" : "#2563eb";
  return (
    <button onClick={() => nav(`/productions?open=${p.id}`)} title={`Consegna ${p.number} · ${p.garment_code} · ${p.pieces} pz${p.lab?.name ? ` · ${p.lab.name}` : ""}`}
      className={`w-full text-left ${compact ? "text-[10px] px-1.5 py-1" : "text-[10px] p-1.5"} mb-0.5 border truncate flex items-center gap-1 bg-white hover:bg-[#FAFAFA] transition-colors`}
      style={{ borderColor: color, borderLeftWidth: 3, color }} data-testid={`cal-delivery-${p.id}`}>
      <Factory size={10} className="shrink-0"/><span className="truncate flex-1">{p.number} · {p.garment_code}</span><span>{p.pieces}pz</span>
    </button>
  );
}

const empty = {
  title: "", description: "", garment_id: "", collection_id: "", order_id: "", assignee_id: "",
  hours_required: 8, hours_per_day: 8,
  start_date: new Date().toISOString().slice(0,10),
  status: "todo", priority: "normal",
};
const STATUSES = ["todo","in_progress","done","blocked"];
const PRIORITIES = ["low","normal","high","urgent"];

const isoDate = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate()+n); return x; };
const startOfWeek = (d) => { const x = new Date(d); const dow = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dow); x.setHours(0,0,0,0); return x; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth()+1, 0);
const monthName = (d) => d.toLocaleString(undefined, { month: "long", year: "numeric" });
const dayKey = (d) => isoDate(d);

const priorityTone = (p) => p === "urgent" ? "red" : p === "high" ? "gold" : "default";
const statusTone = (s) => s === "done" ? "green" : s === "blocked" ? "red" : s === "in_progress" ? "blue" : "default";
const priorityBar = (p) =>
  p === "urgent" ? "#dc2626" : p === "high" ? "#0A0A0A" : p === "low" ? "#a3a3a3" : "#8b7355";

// Build a map of day → list of {task, hours}
function buildDayMap(tasks) {
  const map = new Map();
  for (const t of tasks) {
    const sched = t.schedule && t.schedule.length
      ? t.schedule
      : (t.start_date ? [{ date: t.start_date, hours: t.hours_required || 0 }] : []);
    for (const s of sched) {
      if (!map.has(s.date)) map.set(s.date, []);
      map.get(s.date).push({ task: t, hours: s.hours });
    }
  }
  return map;
}

export default function TasksPage() {
  const { user } = useAuth();
  const { canEdit, canAdmin } = useBrandPermissions();
  const { activeBrand } = useBrand();
  const t = useT();

  const [list, setList] = useState([]);
  const [garments, setGarments] = useState([]);
  const [collections, setCollections] = useState([]);
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  // Default view = WEEK
  const [view, setView] = useState("week");
  const deliveries = useDeliveries();
  const [cursor, setCursor] = useState(new Date());
  const [filters, setFilters] = useState({ search: "", user: "", collection: "", garment: "", status: "", priority: "" });

  const load = () => api.get("/tasks").then(r => setList(r.data));
  useEffect(() => {
    load();
    api.get("/garments").then(r=>setGarments(r.data)).catch(()=>{});
    api.get("/collections").then(r=>setCollections(r.data)).catch(()=>{});
    api.get("/clients").then(r=>setClients(r.data)).catch(()=>{});
    api.get("/orders").then(r=>setOrders(r.data)).catch(()=>{});
    if (activeBrand?.id) {
      api.get(`/brands/${activeBrand.id}/members`)
        .then(r => setUsers(r.data.map(m => ({
          id: m.user_id, name: m.user?.name || m.user?.email || m.name || m.email || "—", email: m.user?.email || m.email, role: m.brand_role,
        }))))
        .catch(() => api.get("/users").then(r=>setUsers(r.data)).catch(()=>{}));
    } else {
      api.get("/users").then(r=>setUsers(r.data)).catch(()=>{});
    }
  }, [activeBrand?.id]);

  // Lookup maps
  const cmap = useMemo(() => Object.fromEntries(collections.map(c=>[c.id, c])), [collections]);
  const gmap = useMemo(() => Object.fromEntries(garments.map(g=>[g.id, g])), [garments]);
  const clmap = useMemo(() => Object.fromEntries(clients.map(cl=>[cl.id, cl])), [clients]);
  const omap = useMemo(() => Object.fromEntries(orders.map(o=>[o.id, o])), [orders]);
  const umap = useMemo(() => Object.fromEntries(users.map(u=>[u.id, u])), [users]);

  // Client name for a task comes only from its order
  const clientNameOf = (task) => {
    const cid = omap[task.order_id]?.client_id;
    return cid ? (clmap[cid]?.name || "—") : "—";
  };

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return list.filter(t => {
      if (filters.user && t.assignee_id !== filters.user) return false;
      if (filters.collection && t.collection_id !== filters.collection) return false;
      if (filters.garment && t.garment_id !== filters.garment) return false;
      if (filters.status && t.status !== filters.status) return false;
      if (filters.priority && t.priority !== filters.priority) return false;
      if (q) {
        const hay = `${t.title || ""} ${t.description || ""} ${cmap[t.collection_id]?.name || ""} ${gmap[t.garment_id]?.name || ""} ${umap[t.assignee_id]?.name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [list, filters, cmap, gmap, umap]);

  const assignableUsers = useMemo(
    () => users.filter(u => u.role !== "client_viewer" && u.role !== "client"),
    [users]
  );
  const garmentsForForm = useMemo(
    () => garments.filter(g => !form.collection_id || g.collection_id === form.collection_id),
    [garments, form.collection_id]
  );

  // --- Summary metrics ---
  const now = new Date(); now.setHours(0,0,0,0);
  const todayIso = isoDate(now);
  const overdueCount = filtered.filter(t => t.status !== "done" && t.end_date && new Date(t.end_date + "T00:00:00") < now).length;
  const todayDayMap = useMemo(() => buildDayMap(filtered), [filtered]);
  const todayCount = (todayDayMap.get(todayIso) || []).length;
  const blockedCount = filtered.filter(t => t.status === "blocked").length;

  // Weekly capacity for current week (based on cursor)
  const weekStart = startOfWeek(cursor);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekHoursScheduled = weekDays.reduce((sum, d) => {
    const items = todayDayMap.get(dayKey(d)) || [];
    return sum + items.reduce((s, it) => s + (it.hours || 0), 0);
  }, 0);
  const workingDays = 5;
  const perMember = 8;
  const weekCapacity = Math.max(assignableUsers.length, 1) * workingDays * perMember;
  const capacityPct = Math.round((weekHoursScheduled / weekCapacity) * 100);

  const open = (r) => {
    setError("");
    if (r) { setForm({ ...empty, ...r, start_date: r.start_date?.slice(0,10) }); setEditId(r.id); }
    else { setForm(empty); setEditId(null); }
    setModal(true);
  };
  useAutoOpenFromQuery(list, open);
  const save = async (e) => {
    e.preventDefault(); setError("");
    try {
      const payload = { ...form, hours_required: Number(form.hours_required)||0, hours_per_day: Number(form.hours_per_day)||8 };
      if (editId) await api.put(`/tasks/${editId}`, payload);
      else await api.post("/tasks", payload);
      setModal(false); setPreview(null); load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm(t("tasks.confirm_delete"))) return; await api.delete(`/tasks/${id}`); load(); };
  const changeStatus = async (task, status) => { await api.patch(`/tasks/${task.id}/status`, null, { params: { status } }); load(); };

  const previewSched = async () => {
    if (!form.start_date || !form.hours_required) return;
    try {
      const r = await api.post("/tasks/preview-schedule", { start_date: form.start_date, hours_required: Number(form.hours_required), hours_per_day: Number(form.hours_per_day)||8 });
      setPreview(r.data);
    } catch { setPreview(null); }
  };

  const VIEWS = [
    { key: "week", label: t("tasks.view.week") },
    { key: "board", label: t("tasks.view.board") },
    { key: "month", label: t("tasks.view.month") },
    { key: "gantt", label: t("tasks.view.gantt") },
    { key: "list", label: t("tasks.view.list") },
  ];

  const enrich = { cmap, gmap, clmap, omap, umap, clientNameOf, t };

  return (
    <div data-testid="tasks-page">
      <PageHeader eyebrow={t("tasks.eyebrow")} title={t("tasks.title")}
        description={t("tasks.desc")}
        action={canEdit && <button data-testid="new-task-btn" onClick={()=>open(null)} className="btn-gold inline-flex items-center gap-2"><Plus size={14}/>{t("tasks.new")}</button>}/>

      {/* Summary row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="tasks-summary">
        <SummaryCard label={t("tasks.summary.overdue")} value={overdueCount} tone={overdueCount > 0 ? "red" : "muted"} icon={<AlertTriangle size={14}/>} testid="summary-overdue"/>
        <SummaryCard label={t("tasks.summary.today")} value={todayCount} tone={todayCount > 0 ? "default" : "muted"} icon={<Clock size={14}/>} testid="summary-today"/>
        <SummaryCard label={t("tasks.summary.blocked")} value={blockedCount} tone={blockedCount > 0 ? "red" : "muted"} icon={<AlertTriangle size={14}/>} testid="summary-blocked"/>
        <SummaryCard label={t("tasks.summary.capacity")} value={`${weekHoursScheduled}h / ${weekCapacity}h`} sub={`${capacityPct}%`} tone={capacityPct > 100 ? "red" : capacityPct > 85 ? "gold" : "muted"} icon={<CalendarClock size={14}/>} testid="summary-capacity"/>
      </div>

      {/* Views + filters */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <div className="flex border border-[#E5E5E5]">
          {VIEWS.map(v => (
            <button key={v.key} onClick={()=>setView(v.key)} data-testid={`tasks-view-${v.key}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${view === v.key ? "bg-[#0A0A0A] text-white" : "text-[#525252] hover:text-[#0A0A0A] hover:bg-[#F5F5F5]"}`}>
              {v.label}
            </button>
          ))}
        </div>

        {/* Single filter bar */}
        <div className="flex flex-wrap gap-2 lg:ml-auto">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#737373]"/>
            <input type="text" placeholder={t("tasks.filter.search")}
              className="input-dark py-1 pl-7 text-xs w-[180px]"
              value={filters.search}
              onChange={(e)=>setFilters({...filters, search: e.target.value})}
              data-testid="filter-search"/>
          </div>
          <select className="input-dark py-1 text-xs max-w-[160px]" value={filters.user} onChange={(e)=>setFilters({...filters, user: e.target.value})} data-testid="filter-user">
            <option value="">{t("tasks.filter.all_assignees")}</option>
            {assignableUsers.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select className="input-dark py-1 text-xs max-w-[160px]" value={filters.collection} onChange={(e)=>setFilters({...filters, collection: e.target.value, garment: ""})} data-testid="filter-collection">
            <option value="">{t("tasks.filter.all_collections")}</option>
            {collections.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className="input-dark py-1 text-xs max-w-[160px]" value={filters.garment} onChange={(e)=>setFilters({...filters, garment: e.target.value})} data-testid="filter-garment">
            <option value="">{t("tasks.filter.all_garments")}</option>
            {garments.filter(g => !filters.collection || g.collection_id === filters.collection).map(g=><option key={g.id} value={g.id}>{g.code} — {g.name}</option>)}
          </select>
          <select className="input-dark py-1 text-xs" value={filters.status} onChange={(e)=>setFilters({...filters, status: e.target.value})} data-testid="filter-status">
            <option value="">{t("tasks.filter.any_status")}</option>
            {STATUSES.map(s=><option key={s} value={s}>{t(`tasks.status.${s}`)}</option>)}
          </select>
          <select className="input-dark py-1 text-xs" value={filters.priority} onChange={(e)=>setFilters({...filters, priority: e.target.value})} data-testid="filter-priority">
            <option value="">{t("tasks.filter.any_priority")}</option>
            {PRIORITIES.map(p=><option key={p} value={p}>{t(`tasks.priority.${p}`)}</option>)}
          </select>
        </div>
      </div>

      {view === "board" && (
        <BoardView tasks={filtered} canEdit={canEdit} canAdmin={canAdmin} onEdit={open} onRemove={remove} enrich={enrich}/>
      )}
      {view === "list" && (
        <ListView tasks={filtered} canEdit={canEdit} onEdit={open} onRemove={remove} enrich={enrich}/>
      )}
      {view === "month" && (
        <MonthView tasks={filtered} cursor={cursor} setCursor={setCursor} onEdit={open} enrich={enrich} deliveries={deliveries}/>
      )}
      {view === "week" && (
        <WeekView tasks={filtered} cursor={cursor} setCursor={setCursor} onEdit={open} enrich={enrich} assignableUsers={assignableUsers} perMember={perMember} deliveries={deliveries}/>
      )}
      {view === "gantt" && (
        <GanttView tasks={filtered} cursor={cursor} setCursor={setCursor} onEdit={open} enrich={enrich}/>
      )}
      {filtered.length === 0 && view === "board" && <Empty title={t("tasks.empty")}/>}

      <Modal open={modal} onClose={()=>{setModal(false);setPreview(null);}} title={editId ? t("tasks.form.edit") : t("tasks.form.new")} wide>
        <form onSubmit={save} className="space-y-6">
          {/* Section: Task */}
          <FormSection title={t("tasks.form.section.task")}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="label-caps block mb-2">{t("tasks.form.title")}</label>
                <input data-testid="task-form-title" required className="input-dark" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})}/>
              </div>
            </div>
          </FormSection>

          {/* Section: Collection / Garment */}
          <FormSection title={t("tasks.form.section.link")}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-caps block mb-2">{t("tasks.form.collection")}</label>
                <select className="input-dark" value={form.collection_id||""} onChange={(e)=>setForm({...form,collection_id:e.target.value, garment_id: ""})} data-testid="task-form-collection">
                  <option value="">—</option>
                  {collections.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-caps block mb-2">{t("tasks.form.garment")}</label>
                <select className="input-dark" value={form.garment_id||""} onChange={(e)=>setForm({...form,garment_id:e.target.value})}>
                  <option value="">—</option>
                  {garmentsForForm.map(g=><option key={g.id} value={g.id}>{g.code} — {g.name}</option>)}
                </select>
              </div>
            </div>
          </FormSection>

          {/* Section: Assignee */}
          <FormSection title={t("tasks.form.section.assignee")}>
            <div>
              <label className="label-caps block mb-2">{t("tasks.form.assignee")}</label>
              <select className="input-dark" value={form.assignee_id||""} onChange={(e)=>setForm({...form,assignee_id:e.target.value})}>
                <option value="">—</option>
                {assignableUsers.map(u=><option key={u.id} value={u.id}>{u.name} ({(u.role||"").replaceAll("_"," ")})</option>)}
              </select>
            </div>
          </FormSection>

          {/* Section: Dates */}
          <FormSection title={t("tasks.form.section.dates")}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-caps block mb-2">{t("tasks.form.start")}</label>
                <input data-testid="task-form-start" type="date" required className="input-dark" value={form.start_date} onChange={(e)=>setForm({...form,start_date:e.target.value})}/>
              </div>
              <div className="flex items-end">
                <button type="button" onClick={previewSched} className="btn-ghost text-xs" data-testid="task-preview-btn">{t("tasks.form.preview")}</button>
                {preview && (
                  <div className="ml-3 text-xs text-[#525252]">
                    → <span className="text-[#0A0A0A]">{preview.days_needed}</span> {t("tasks.form.preview_result")} <span className="text-[#0A0A0A]">{preview.end_date}</span>
                  </div>
                )}
              </div>
            </div>
          </FormSection>

          {/* Section: Hours */}
          <FormSection title={t("tasks.form.section.hours")}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-caps block mb-2">{t("tasks.form.hours_required")}</label>
                <input data-testid="task-form-hours" type="number" step="0.5" className="input-dark" value={form.hours_required} onChange={(e)=>setForm({...form,hours_required:e.target.value})}/>
              </div>
              <div>
                <label className="label-caps block mb-2">{t("tasks.form.hours_per_day")}</label>
                <input data-testid="task-form-hpd" type="number" step="0.5" className="input-dark" value={form.hours_per_day} onChange={(e)=>setForm({...form,hours_per_day:e.target.value})}/>
              </div>
            </div>
          </FormSection>

          {/* Section: Priority & Status */}
          <FormSection title={t("tasks.form.section.meta")}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-caps block mb-2">{t("tasks.form.priority")}</label>
                <select className="input-dark" value={form.priority} onChange={(e)=>setForm({...form,priority:e.target.value})}>
                  {PRIORITIES.map(p=><option key={p} value={p}>{t(`tasks.priority.${p}`)}</option>)}
                </select>
              </div>
              <div>
                <label className="label-caps block mb-2">{t("tasks.form.status")}</label>
                <select className="input-dark" value={form.status} onChange={(e)=>setForm({...form,status:e.target.value})}>
                  {STATUSES.map(s=><option key={s} value={s}>{t(`tasks.status.${s}`)}</option>)}
                </select>
              </div>
            </div>
          </FormSection>

          {/* Section: Description */}
          <FormSection title={t("tasks.form.section.description")}>
            <textarea rows={3} className="input-dark" value={form.description||""} onChange={(e)=>setForm({...form,description:e.target.value})}/>
          </FormSection>

          {error && <div className="text-red-600 text-sm">{error}</div>}
          <div className="flex justify-end gap-3 pt-2 border-t border-[#E5E5E5]">
            <button type="button" onClick={()=>{setModal(false);setPreview(null);}} className="btn-ghost">{t("common.cancel")}</button>
            <button data-testid="task-form-save" className="btn-gold">{t("common.save")}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// -------------------- Sub-components --------------------

function FormSection({ title, children }) {
  return (
    <div className="border-l-2 border-[#0A0A0A] pl-4">
      <div className="label-caps mb-3 text-[#0A0A0A]">{title}</div>
      {children}
    </div>
  );
}

function SummaryCard({ label, value, sub, tone = "muted", icon, testid }) {
  const toneCls =
    tone === "red" ? "border-red-300 bg-red-50" :
    tone === "gold" ? "border-[#8b7355] bg-[#faf6ef]" :
    tone === "default" ? "border-[#0A0A0A] bg-[#FAFAFA]" :
    "border-[#E5E5E5] bg-white";
  const valueCls =
    tone === "red" ? "text-red-700" :
    tone === "gold" ? "text-[#8b7355]" :
    "text-[#0A0A0A]";
  return (
    <div className={`border ${toneCls} p-4 flex items-center justify-between`} data-testid={testid}>
      <div>
        <div className="label-caps text-[10px] text-[#525252]">{label}</div>
        <div className={`font-serif text-2xl mt-1 ${valueCls}`}>{value}</div>
        {sub && <div className="text-[10px] text-[#737373] mt-0.5">{sub}</div>}
      </div>
      <div className="text-[#737373]">{icon}</div>
    </div>
  );
}

function TaskMeta({ task, enrich, compact = false }) {
  const { cmap, gmap, umap, clientNameOf, t } = enrich;
  const coll = cmap[task.collection_id];
  const gar = gmap[task.garment_id];
  const assn = umap[task.assignee_id];
  const client = clientNameOf(task);
  return (
    <div className={`space-y-1 text-[11px] text-[#525252] ${compact ? "" : "mt-2"}`}>
      <div className="flex items-center gap-1.5"><User size={11}/> {client}</div>
      {(coll || gar) && (
        <div className="flex items-center gap-1.5">
          <Layers size={11}/>
          <span className="truncate">{coll?.name || "—"}{gar ? ` · ${gar.code}` : ""}</span>
        </div>
      )}
      <div className="flex items-center gap-1.5"><Package size={11}/> {assn?.name || t("tasks.unassigned")}</div>
    </div>
  );
}

function BoardView({ tasks, canEdit, canAdmin, onEdit, onRemove, enrich }) {
  const { t } = enrich;
  const grouped = STATUSES.map(s => ({ status: s, items: tasks.filter(x=>x.status===s) }));
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="tasks-board">
      {grouped.map(col => (
        <div key={col.status} className="border border-[#E5E5E5] bg-[#FAFAFA] p-4">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-[#E5E5E5]">
            <div className="label-caps">{t(`tasks.status.${col.status}`)}</div>
            <div className="text-xs text-[#525252]">{col.items.length}</div>
          </div>
          <div className="space-y-3">
            {col.items.map(task => (
              <div key={task.id}
                className="relative bg-white border border-[#E5E5E5] p-3 hover:border-[#0A0A0A] transition-colors cursor-pointer group"
                onClick={()=>canEdit && onEdit(task)}
                data-testid={`task-card-${task.id}`}>
                {/* Priority left bar */}
                <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: priorityBar(task.priority) }}/>
                <div className="pl-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-medium text-[#0A0A0A] leading-tight">{task.title}</div>
                    <Chip tone={priorityTone(task.priority)}>{t(`tasks.priority.${task.priority}`)}</Chip>
                  </div>
                  <TaskMeta task={task} enrich={enrich}/>
                  <div className="mt-3 pt-2 border-t border-[#F0F0F0] flex items-center justify-between text-[10px] text-[#737373]">
                    <div className="flex items-center gap-1"><CalendarClock size={10}/> {task.start_date} → {task.end_date || task.start_date}</div>
                    <div>{task.hours_required}h</div>
                  </div>
                  {canEdit && (
                    <div className="mt-2 flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={(e)=>{e.stopPropagation(); onEdit(task);}} className="text-[#525252] hover:text-[#0A0A0A]" data-testid={`task-edit-${task.id}`}><Edit3 size={12}/></button>
                      {canAdmin && <button onClick={(e)=>{e.stopPropagation(); onRemove(task.id);}} className="text-[#525252] hover:text-red-600" data-testid={`task-delete-${task.id}`}><Trash2 size={12}/></button>}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {col.items.length === 0 && <div className="text-xs text-[#737373] italic text-center py-4">—</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function ListView({ tasks, canEdit, onEdit, onRemove, enrich }) {
  const { cmap, gmap, umap, clientNameOf, t } = enrich;
  const sorted = [...tasks].sort((a,b)=>String(a.start_date).localeCompare(String(b.start_date)));
  return (
    <div className="border border-[#E5E5E5] bg-white overflow-x-auto" data-testid="tasks-list-view">
      <table className="w-full text-xs">
        <thead className="bg-[#FAFAFA]">
          <tr className="text-[#525252] uppercase tracking-widest border-b border-[#E5E5E5]">
            <th className="text-left p-3">{t("tasks.col.task")}</th>
            <th className="text-left p-3">{t("tasks.col.client")}</th>
            <th className="text-left p-3">{t("tasks.col.collection_garment")}</th>
            <th className="text-left p-3">{t("tasks.col.assignee")}</th>
            <th className="text-left p-3">{t("tasks.col.dates")}</th>
            <th className="text-right p-3">{t("tasks.col.hours")}</th>
            <th className="text-left p-3">{t("tasks.col.status")}</th>
            <th className="text-left p-3">{t("tasks.col.priority")}</th>
            <th className="w-16"></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(task => (
            <tr key={task.id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`task-row-${task.id}`}>
              <td className="p-3 max-w-[240px]">
                <div className="text-[#0A0A0A] font-medium">{task.title}</div>
                {task.description && <div className="text-[#737373] line-clamp-1 text-[10px] mt-0.5">{task.description}</div>}
              </td>
              <td className="p-3 text-[#525252]">{clientNameOf(task)}</td>
              <td className="p-3 text-[#525252]">
                {cmap[task.collection_id]?.name || "—"}
                {task.garment_id && <span className="text-[#737373]"> · {gmap[task.garment_id]?.code || ""}</span>}
              </td>
              <td className="p-3 text-[#525252]">{umap[task.assignee_id]?.name || <span className="text-[#a3a3a3]">—</span>}</td>
              <td className="p-3 text-[#525252] whitespace-nowrap">{task.start_date} → {task.end_date || task.start_date}</td>
              <td className="p-3 text-right text-[#525252]">{task.hours_required}h</td>
              <td className="p-3"><Chip tone={statusTone(task.status)}>{t(`tasks.status.${task.status}`)}</Chip></td>
              <td className="p-3"><Chip tone={priorityTone(task.priority)}>{t(`tasks.priority.${task.priority}`)}</Chip></td>
              <td className="p-3 text-right whitespace-nowrap">
                {canEdit && <>
                  <button onClick={()=>onEdit(task)} className="text-[#525252] hover:text-[#0A0A0A] mr-2"><Edit3 size={12}/></button>
                  <button onClick={()=>onRemove(task.id)} className="text-[#525252] hover:text-red-600"><Trash2 size={12}/></button>
                </>}
              </td>
            </tr>
          ))}
          {sorted.length === 0 && (
            <tr><td colSpan={9} className="text-center text-[#737373] italic py-8">{t("tasks.empty")}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function MonthView({ tasks, cursor, setCursor, onEdit, enrich, deliveries }) {
  const { t } = enrich;
  const dayMap = useMemo(() => buildDayMap(tasks), [tasks]);
  const first = startOfMonth(cursor);
  const gridStart = startOfWeek(first);
  const last = endOfMonth(cursor);
  const gridEnd = addDays(startOfWeek(last), 41);
  const days = [];
  for (let d = new Date(gridStart); d <= gridEnd; d = addDays(d, 1)) days.push(new Date(d));
  const todayIso = isoDate(new Date());

  return (
    <div className="border border-[#E5E5E5] bg-white p-4" data-testid="tasks-month-view">
      <div className="flex items-center justify-between mb-4">
        <div className="font-serif text-2xl capitalize">{monthName(cursor)}</div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-xs" onClick={()=>setCursor(addDays(startOfMonth(cursor), -1))} data-testid="cal-prev"><ChevronLeft size={14}/></button>
          <button className="btn-ghost text-xs" onClick={()=>setCursor(new Date())} data-testid="cal-today">{t("tasks.today")}</button>
          <button className="btn-ghost text-xs" onClick={()=>setCursor(addDays(endOfMonth(cursor), 1))} data-testid="cal-next"><ChevronRight size={14}/></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px bg-[#E5E5E5] text-[10px] uppercase tracking-widest text-[#737373]">
        {["Lun","Mar","Mer","Gio","Ven","Sab","Dom"].map(d => <div key={d} className="bg-[#FAFAFA] p-2 text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-px bg-[#E5E5E5]" data-testid="month-grid">
        {days.map((d) => {
          const key = dayKey(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const weekend = d.getDay() === 0 || d.getDay() === 6;
          const items = dayMap.get(key) || [];
          const dels = deliveries.get(key) || [];
          const bg = weekend ? "bg-[#F5F5F5]" : "bg-white";
          return (
            <div key={key} className={`min-h-[110px] p-1.5 ${bg} ${inMonth ? "" : "opacity-40"} ${key === todayIso ? "ring-2 ring-inset ring-[#0A0A0A]" : ""}`}>
              <div className="text-[11px] mb-1 flex items-center justify-between">
                <span className={`${key === todayIso ? "text-[#0A0A0A] font-medium" : "text-[#737373]"}`}>{d.getDate()}</span>
                {items.length > 3 && <span className="text-[9px] text-[#525252]">+{items.length - 3}</span>}
              </div>
              {dels.map(p => <DeliveryChip key={p.id} p={p} compact/>)}
              {items.slice(0, 3).map((it) => {
                const overdue = it.task.status !== "done" && it.task.end_date && new Date(it.task.end_date + "T00:00:00") < new Date(todayIso + "T00:00:00");
                return (
                  <button key={it.task.id + key} onClick={()=>onEdit(it.task)} title={`${it.task.title} · ${it.hours}h`}
                    className="w-full text-left text-[10px] px-1.5 py-1 mb-0.5 border truncate flex items-center gap-1 hover:bg-[#FAFAFA] transition-colors"
                    style={{ borderColor: priorityBar(it.task.priority), borderLeftWidth: 3 }}
                    data-testid={`month-cell-task-${it.task.id}`}>
                    <span className="text-[#0A0A0A] truncate flex-1">{it.task.title}</span>
                    <span className="text-[#737373]">{it.hours}h</span>
                    {overdue && <AlertTriangle size={9} className="text-red-600"/>}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ tasks, cursor, setCursor, onEdit, enrich, assignableUsers, perMember, deliveries }) {
  const { t, cmap, gmap, clientNameOf } = enrich;
  const start = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const todayIso = isoDate(new Date());

  // Group tasks by assignee + day
  const byMemberDay = useMemo(() => {
    const map = new Map(); // assignee_id -> Map(dayKey -> [{task, hours}])
    for (const task of tasks) {
      const sched = task.schedule && task.schedule.length
        ? task.schedule
        : (task.start_date ? [{ date: task.start_date, hours: task.hours_required || 0 }] : []);
      const aid = task.assignee_id || "unassigned";
      if (!map.has(aid)) map.set(aid, new Map());
      const inner = map.get(aid);
      for (const s of sched) {
        if (!inner.has(s.date)) inner.set(s.date, []);
        inner.get(s.date).push({ task, hours: s.hours });
      }
    }
    return map;
  }, [tasks]);

  // Build ordered rows: all assignable users first, then "unassigned" if any tasks
  const rows = useMemo(() => {
    const r = assignableUsers.map(u => ({ id: u.id, name: u.name }));
    if (byMemberDay.has("unassigned")) r.push({ id: "unassigned", name: t("tasks.unassigned") });
    return r;
  }, [assignableUsers, byMemberDay, t]);

  return (
    <div className="border border-[#E5E5E5] bg-white p-4" data-testid="tasks-week-view">
      <div className="flex items-center justify-between mb-4">
        <div className="font-serif text-2xl">
          {t("tasks.week.of")} {start.toLocaleDateString("it-IT", { day: "numeric", month: "long" })}
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-xs" onClick={()=>setCursor(addDays(start, -7))} data-testid="week-prev"><ChevronLeft size={14}/></button>
          <button className="btn-ghost text-xs" onClick={()=>setCursor(new Date())} data-testid="week-today">{t("tasks.today")}</button>
          <button className="btn-ghost text-xs" onClick={()=>setCursor(addDays(start, 7))} data-testid="week-next"><ChevronRight size={14}/></button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          {/* Header: days */}
          <div className="grid" style={{ gridTemplateColumns: `200px repeat(7, 1fr)` }}>
            <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[#525252] border-b border-[#E5E5E5]">
              {t("tasks.week.member")}
            </div>
            {days.map(d => {
              const weekend = d.getDay() === 0 || d.getDay() === 6;
              const isToday = dayKey(d) === todayIso;
              return (
                <div key={dayKey(d)}
                  className={`px-2 py-2 text-center border-b border-l border-[#E5E5E5] ${weekend ? "bg-[#F5F5F5]" : ""} ${isToday ? "bg-[#0A0A0A] text-white" : ""}`}>
                  <div className={`text-[10px] uppercase tracking-widest ${isToday ? "text-white/70" : "text-[#737373]"}`}>
                    {d.toLocaleDateString("it-IT", { weekday: "short" })}
                  </div>
                  <div className={`font-serif text-lg ${isToday ? "text-white" : "text-[#0A0A0A]"}`}>{d.getDate()}</div>
                </div>
              );
            })}
          </div>

          {days.some(d => deliveries.has(dayKey(d))) && (
            <div className="grid border-b border-[#E5E5E5]" style={{ gridTemplateColumns: `200px repeat(7, 1fr)` }} data-testid="week-row-deliveries">
              <div className="px-3 py-2 border-r border-[#E5E5E5] text-sm font-medium flex items-center gap-2"><Factory size={14}/> Consegne produzioni</div>
              {days.map(d => (
                <div key={dayKey(d)} className="p-1 border-l border-[#E5E5E5]">
                  {(deliveries.get(dayKey(d)) || []).map(p => <DeliveryChip key={p.id} p={p}/>)}
                </div>))}
            </div>
          )}
          {/* Rows */}
          {rows.map(member => {
            const inner = byMemberDay.get(member.id) || new Map();
            const totalHours = days.reduce((s, d) => {
              const list = inner.get(dayKey(d)) || [];
              return s + list.reduce((a, it) => a + (it.hours || 0), 0);
            }, 0);
            const workingDays = days.filter(d => d.getDay() !== 0 && d.getDay() !== 6).length;
            const capacity = workingDays * perMember;
            const over = totalHours > capacity;
            return (
              <div key={member.id} className="grid border-b border-[#E5E5E5]" style={{ gridTemplateColumns: `200px repeat(7, 1fr)` }} data-testid={`week-row-${member.id}`}>
                <div className="px-3 py-2 border-r border-[#E5E5E5] flex flex-col justify-center">
                  <div className="text-sm text-[#0A0A0A] font-medium truncate">{member.name}</div>
                  <div className={`text-[10px] mt-1 ${over ? "text-red-600 font-medium" : "text-[#737373]"}`}>
                    {totalHours}h / {capacity}h
                    {over && <span className="ml-1 uppercase tracking-widest">· {t("tasks.week.capacity_over")}</span>}
                  </div>
                  {/* capacity bar */}
                  <div className="mt-1 h-1 bg-[#F0F0F0] w-full">
                    <div className={`h-full ${over ? "bg-red-500" : "bg-[#0A0A0A]"}`}
                      style={{ width: `${Math.min(100, (totalHours / Math.max(capacity, 1)) * 100)}%` }}/>
                  </div>
                </div>
                {days.map(d => {
                  const weekend = d.getDay() === 0 || d.getDay() === 6;
                  const key = dayKey(d);
                  const items = inner.get(key) || [];
                  const dayHours = items.reduce((s, it) => s + (it.hours || 0), 0);
                  const dayOver = dayHours > perMember;
                  const isToday = key === todayIso;
                  return (
                    <div key={key}
                      className={`p-1 border-l border-[#E5E5E5] min-h-[80px] ${weekend ? "bg-[#F5F5F5]" : ""} ${isToday ? "ring-1 ring-inset ring-[#0A0A0A]" : ""}`}>
                      {items.map(it => (
                        <button key={it.task.id + key} onClick={()=>onEdit(it.task)}
                          className="w-full text-left mb-1 p-1.5 border border-[#E5E5E5] bg-white hover:border-[#0A0A0A] transition-colors"
                          style={{ borderLeftWidth: 3, borderLeftColor: priorityBar(it.task.priority) }}
                          title={`${it.task.title} · ${it.hours}h · ${clientNameOf(it.task)}`}
                          data-testid={`week-cell-task-${it.task.id}`}>
                          <div className="text-[10px] text-[#0A0A0A] font-medium truncate leading-tight">{it.task.title}</div>
                          <div className="text-[9px] text-[#737373] truncate">
                            {cmap[it.task.collection_id]?.name || clientNameOf(it.task)}
                            {it.task.garment_id && ` · ${gmap[it.task.garment_id]?.code || ""}`}
                          </div>
                          <div className="text-[9px] text-[#525252] mt-0.5">{it.hours}h</div>
                        </button>
                      ))}
                      {items.length > 0 && (
                        <div className={`text-[9px] uppercase tracking-widest text-center pt-1 ${dayOver ? "text-red-600 font-medium" : "text-[#737373]"}`}>
                          {dayHours}h
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {rows.length === 0 && (
            <div className="p-8 text-center text-xs text-[#737373] italic">{t("tasks.empty")}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function GanttView({ tasks, cursor, setCursor, onEdit, enrich }) {
  const { umap, cmap, t, clientNameOf } = enrich;
  const DAYS = 30;
  const start = startOfWeek(cursor);
  const days = Array.from({ length: DAYS }, (_, i) => addDays(start, i));

  const groups = useMemo(() => {
    const g = new Map();
    for (const task of tasks) {
      const key = task.assignee_id || "unassigned";
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(task);
    }
    return [...g.entries()];
  }, [tasks]);

  const dayW = 32;
  const rowH = 40;
  const gridW = DAYS * dayW;
  const labelW = 220;

  const dateIndex = (iso) => {
    if (!iso) return null;
    const d = new Date(iso + "T00:00:00");
    const diff = Math.floor((d - start) / 86400000);
    if (diff < 0 || diff >= DAYS) return null;
    return diff;
  };

  const todayIdx = dateIndex(isoDate(new Date()));

  return (
    <div className="border border-[#E5E5E5] bg-white p-4" data-testid="tasks-gantt-view">
      <div className="flex items-center justify-between mb-4">
        <div className="font-serif text-2xl">
          {start.toLocaleDateString("it-IT", { day: "numeric", month: "long" })} — 30 {t("tasks.today").toLowerCase() === "oggi" ? "giorni" : "days"}
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-xs" onClick={()=>setCursor(addDays(start, -14))} data-testid="gantt-prev"><ChevronLeft size={14}/></button>
          <button className="btn-ghost text-xs" onClick={()=>setCursor(new Date())} data-testid="gantt-today">{t("tasks.today")}</button>
          <button className="btn-ghost text-xs" onClick={()=>setCursor(addDays(start, 14))} data-testid="gantt-next"><ChevronRight size={14}/></button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div style={{ minWidth: labelW + gridW }}>
          {/* Header row */}
          <div className="flex sticky top-0 bg-white z-10 border-b border-[#E5E5E5]">
            <div style={{ width: labelW }} className="shrink-0 px-3 py-2 text-[10px] uppercase tracking-widest text-[#525252]">
              {t("tasks.col.assignee")} / {t("tasks.col.task")}
            </div>
            <div className="flex" style={{ width: gridW }}>
              {days.map((d) => {
                const weekend = d.getDay() === 0 || d.getDay() === 6;
                return (
                  <div key={d.toISOString()} style={{ width: dayW }}
                    className={`text-center py-2 text-[10px] border-l border-[#E5E5E5] ${weekend ? "bg-[#F5F5F5] text-[#737373]" : "text-[#525252]"}`}>
                    <div>{d.toLocaleDateString("it-IT", { weekday: "short" }).slice(0,2)}</div>
                    <div className="text-[#0A0A0A] text-xs">{d.getDate()}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rows */}
          {groups.map(([key, items]) => {
            const user = umap[key];
            const label = user?.name || (key === "unassigned" ? t("tasks.unassigned") : key);
            return (
              <div key={key} className="border-b border-[#E5E5E5]">
                {/* Group header */}
                <div className="flex items-center bg-[#FAFAFA]">
                  <div style={{ width: labelW }} className="shrink-0 px-3 py-2 text-xs text-[#0A0A0A] uppercase tracking-widest font-medium">
                    {label} <span className="text-[#737373] normal-case tracking-normal">· {items.length}</span>
                  </div>
                  <div className="relative" style={{ width: gridW, height: 28 }}>
                    {days.map((d, i) => {
                      const weekend = d.getDay() === 0 || d.getDay() === 6;
                      return weekend ? (
                        <div key={d.toISOString()} className="absolute top-0 bottom-0 bg-[#F5F5F5]" style={{ left: i * dayW, width: dayW }}/>
                      ) : null;
                    })}
                    {todayIdx !== null && (
                      <div className="absolute top-0 bottom-0 w-px bg-red-500/60" style={{ left: todayIdx * dayW + dayW / 2 }}/>
                    )}
                  </div>
                </div>
                {/* Task rows */}
                {items.map(task => {
                  const s = dateIndex(task.start_date);
                  const e = dateIndex(task.end_date) ?? s;
                  const rendered = s !== null || e !== null;
                  const left = Math.max(0, s ?? 0);
                  const right = Math.min(DAYS - 1, e ?? left);
                  const width = Math.max(1, right - left + 1) * dayW - 4;
                  const bg = task.status === "done" ? "#16a34a" : task.status === "blocked" ? "#dc2626" : task.status === "in_progress" ? "#0A0A0A" : "#525252";
                  return (
                    <div key={task.id} className="flex items-center hover:bg-[#FAFAFA] relative" style={{ height: rowH }}>
                      <div style={{ width: labelW }} className="shrink-0 px-3 py-1 text-xs truncate">
                        <div className="text-[#0A0A0A] truncate" title={task.title}>{task.title}</div>
                        <div className="text-[10px] text-[#737373] truncate">
                          {cmap[task.collection_id]?.name || clientNameOf(task)}
                        </div>
                      </div>
                      <div className="relative" style={{ width: gridW, height: rowH }}>
                        {/* weekend shading */}
                        {days.map((d, i) => {
                          const weekend = d.getDay() === 0 || d.getDay() === 6;
                          return weekend ? (
                            <div key={d.toISOString()} className="absolute top-0 bottom-0 bg-[#F5F5F5]" style={{ left: i * dayW, width: dayW }}/>
                          ) : null;
                        })}
                        {/* today marker */}
                        {todayIdx !== null && (
                          <div className="absolute top-0 bottom-0 w-px bg-red-500/60" style={{ left: todayIdx * dayW + dayW / 2 }}/>
                        )}
                        {rendered && (
                          <button
                            onClick={() => onEdit(task)}
                            title={`${task.title} · ${task.start_date} → ${task.end_date} · ${task.hours_required}h`}
                            data-testid={`gantt-bar-${task.id}`}
                            className="absolute top-1/2 -translate-y-1/2 text-[10px] px-2 truncate hover:brightness-110 transition-all border shadow-sm flex items-center gap-1"
                            style={{
                              left: left * dayW + 2,
                              width,
                              height: rowH - 12,
                              background: bg,
                              borderColor: priorityBar(task.priority),
                              borderLeftWidth: 4,
                              color: "#fff",
                              lineHeight: `${rowH - 14}px`,
                            }}
                          >
                            <span className="truncate flex-1 text-left">{task.title}</span>
                            <span className="opacity-80 shrink-0">{task.hours_required}h</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {groups.length === 0 && (
            <div className="p-8 text-xs text-[#737373] italic text-center">{t("tasks.empty")}</div>
          )}
        </div>
      </div>
    </div>
  );
}
